import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { ReactComponent as Chevron } from '../../../../../assets/icons/v3/chevron-down.svg';
import { LOADING_TABLE_ROWS_V3 } from '../../../../constants/constants';
import { CompanyContext } from '../../../../context/company/companyContext';
import { useScrollState } from '../../../../hooks/useScrollState/useScrollState';
import { IStyledTable, ITableColumn } from '../../../../interfaces/tables';
import { TRANSLATIONS_GLOBAL } from '../../../../translations/commons';
import BdsTooltip from '../../BdsTooltip/BdsTooltip';
import EmptyCard from '../../Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../Cards/ErrorCard/ErrorCard';
import StyledTableRow from '../StyledTableRow/StyledTableRow';

import './StyledTable.scss';

const StyledTable = ({
  columns,
  data,
  top = 0,
  activeRow,
  emptyCard,
  noFilterDataCard,
  filters,
  error,
  loading,
  dataKey = 'id',
  checkProps,
  initialSortState = [],
  errorAction,
  onRowClick,
  onChange,
  sortData,
  showShadowHeader = true
}: IStyledTable) => {
  const { t } = useTranslation();
  const { companyData, isLoadingCompanyData } = useContext(CompanyContext);
  const [columnsByCountry, setColumnsByCountry] = useState<ITableColumn[]>([]);
  const [isAscending, setIsAscending] = useState<boolean[]>(initialSortState);
  const [openTooltip, setOpenTooltip] = useState(false);
  const headerRef = useRef<HTMLTableSectionElement | null>(null);

  const isScrolled = useScrollState(400, 0);

  useEffect(() => {
    setColumnsByCountry(filterColumnsByCountry());
  }, [companyData, columns]);

  useEffect(() => {
    if (!isAscending.length) {
      const newIsAscending = columnsByCountry.map(() => false);
      setIsAscending(newIsAscending);
    }
  }, [columnsByCountry]);

  const filterColumnsByCountry = useCallback(() => {
    return columns.filter((column) => !column.hideIn?.includes(companyData?.locale.toLowerCase()));
  }, [columns, companyData]);

  const reloadPage = () => {
    window.location.reload();
  };

  const isLoading = useMemo(() => isLoadingCompanyData || loading, [isLoadingCompanyData, loading]);

  const showEmptyTable = useMemo(() => !data.length && !isLoading, [data, isLoading]);

  const hasFilters = useMemo(() => filters && (filters.filterValue || filters.searchValue), [filters]);

  const onSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event?.stopPropagation();
      if (!checkProps || !data) return;
      checkProps.onCheckAll();
    },
    [checkProps, data]
  );

  const onCheckRow = useCallback(
    (key: string) => {
      if (!checkProps) return;
      checkProps.onCheckRow(key);
    },
    [checkProps]
  );

  const onSort = useCallback(
    (column: ITableColumn, index: number) => {
      if (!column.isSortable || !sortData) return;
      const newIsAscending = [...isAscending];
      newIsAscending[index] = !newIsAscending[index];
      setIsAscending(newIsAscending);
      sortData(column.key, newIsAscending[index]);
    },
    [isAscending, sortData]
  );

  useEffect(() => {
    if (!headerRef.current || !showShadowHeader) return;
    if (isScrolled) {
      headerRef.current.classList.add('shadow');
    } else {
      headerRef.current.classList.remove('shadow');
    }
  }, [isScrolled, showShadowHeader]);

  if (error && !isLoading) {
    return (
      <div className="styled-table-error-card ">
        <div className="styled-table-error-card-container">
          <ErrorCard
            primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
            secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
            action={errorAction || reloadPage}
            buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
            center={true}
            className="error-card-center"
          />
        </div>
      </div>
    );
  }

  if (showEmptyTable && hasFilters && noFilterDataCard) {
    return (
      <div className="styled-table-error-card ">
        <div className="styled-table-error-card-container">
          <EmptyCard
            primaryText={t(noFilterDataCard.primaryText)}
            secondaryText={t(noFilterDataCard.secondaryText as string)}
            className="error-card-center"
            center={true}
          />
        </div>
      </div>
    );
  }

  if (showEmptyTable && emptyCard) {
    return (
      <div className="styled-table-error-card ">
        <div className="styled-table-error-card-container">
          <EmptyCard
            primaryText={t(emptyCard.primaryText)}
            secondaryText={t(emptyCard.secondaryText as string)}
            buttonText={t(emptyCard.buttonText as string)}
            buttonVariant="primary"
            action={emptyCard.action}
            center={true}
            className="error-card-center"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="styled-table-content-container ">
      <table className="custom-table">
        <thead className="table-header whitespace-nowrap" style={{ top }} ref={headerRef}>
          <tr>
            {checkProps && (
              <th className="styled-table-column checkbox">
                <BdsTooltip
                  message={TRANSLATIONS_GLOBAL.tooltip}
                  open={openTooltip}
                  position="bottom-left"
                  showPointer={true}
                >
                  <Checkbox
                    className="checkbox"
                    onMouseEnter={() => setOpenTooltip(true)}
                    onMouseLeave={() => setOpenTooltip(false)}
                    checked={checkProps.allSelected}
                    disabled={loading}
                    onChange={onSelectAll}
                  />
                </BdsTooltip>
              </th>
            )}
            {columnsByCountry.map((column, index) => (
              <th
                className={`styled-table-column ${column.class} ${
                  column.isSortable ? 'cursor-pointer' : 'cursor-default'
                }`}
                key={`${column.name}-header`}
                onClick={() => onSort(column, index)}
              >
                {t(column.name)}
                {column.isSortable && <Chevron className={`sort-chevron ${isAscending[index] ? 'asc' : 'desc'}`} />}
              </th>
            ))}
            <th className="more-info-header" />
          </tr>
        </thead>

        <tbody className="table-body">
          {!isLoading &&
            data?.map((row, index) => {
              const rowWithKey = row[dataKey]?.value !== undefined ? `${row[dataKey].value}/${index}` : row[dataKey];
              return (
                <StyledTableRow
                  row={row}
                  columns={columnsByCountry}
                  dataKey={dataKey}
                  key={`${rowWithKey}`}
                  {...(checkProps && {
                    checked: !!checkProps.checkedRows[rowWithKey],
                    onCheck: () => onCheckRow(rowWithKey)
                  })}
                  onChange={
                    onChange &&
                    ((field, value) => {
                      onChange(`${field}/${index}`, value);
                    })
                  }
                  onClick={onRowClick}
                  activeRow={activeRow}
                />
              );
            })}
        </tbody>
      </table>
      {isLoading && (
        <div className="empty-table">
          {[...Array(LOADING_TABLE_ROWS_V3).keys()].map(() => (
            <Skeleton
              className="styled-table-skeleton"
              key={uuid()}
              animation="wave"
              variant="text"
              width={'100%'}
              height={60}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StyledTable;
